import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./Dialog";
import { exercisePreviews } from "../data/receiveSection";
interface ExerciseLightboxProps {
  selectedIndex: number | null;
  onSelectedIndexChange: (index: number | null) => void;
}
interface TouchPosition {
  x: number;
  y: number;
}
export function ExerciseLightbox({
  selectedIndex,
  onSelectedIndexChange
}: ExerciseLightboxProps) {
  const shouldReduceMotion = Boolean(useReducedMotion());
  const touchStart = useRef<TouchPosition | null>(null);
  const [hasError, setHasError] = useState(false);
  const isOpen = selectedIndex !== null;
  const activeIndex = selectedIndex ?? 0;
  const activeImage = exercisePreviews[activeIndex];
  const showPrevious = () => {
    onSelectedIndexChange((activeIndex - 1 + exercisePreviews.length) % exercisePreviews.length);
  };
  const showNext = () => {
    onSelectedIndexChange((activeIndex + 1) % exercisePreviews.length);
  };
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showPrevious();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        showNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, isOpen]);
  useEffect(() => {
    setHasError(false);
  }, [activeIndex]);
  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY
    };
  };
  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!touchStart.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStart.current.x;
    const deltaY = touch.clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (deltaX > 0) showPrevious();else showNext();
  };
  return <Dialog open={isOpen} onOpenChange={open => {
    if (!open) onSelectedIndexChange(null);
  }} data-id="element-34" data-mp="35smybaj8japg">
      <DialogContent showCloseButton className="w-[calc(100vw-1.5rem)] max-w-[1120px] border border-[#333333] bg-[#090B0D]/95 p-3 text-[#F8F8F6] shadow-[0_30px_100px_rgba(0,0,0,0.75)] backdrop-blur-xl sm:p-5" data-id="element-35" data-mp="3aqod3j9monv4">
        <DialogTitle className="sr-only" data-id="element-36" data-mp="3oqfnzb4ulo4w">Vista ampliada del ejercicio</DialogTitle>
        <DialogDescription className="sr-only" data-id="element-37" data-mp="3oocyqtbirz18">
          Usa las flechas para recorrer las seis vistas previas. Presiona Escape para cerrar.
        </DialogDescription>

        <div className="relative flex min-h-[70vh] touch-pan-y items-center justify-center overflow-hidden rounded-xl bg-[#111519]" onClick={event => {
        if (event.target === event.currentTarget) onSelectedIndexChange(null);
      }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-38" data-mp="1ucyayi22k9o4">
          {hasError ? <div className="flex flex-col items-center gap-3 text-center text-sm text-[#B7BBC0]" data-id="element-39" data-mp="1932573b9yx65">
              <ImageOff aria-hidden="true" className="h-10 w-10 text-[#F2B84B]" data-id="element-40" data-mp="25qq5057gy30w" />
              Vista previa no disponible
            </div> : <motion.img key={activeImage.src} src={activeImage.src} alt={activeImage.alt} className="max-h-[78vh] max-w-full object-contain" initial={shouldReduceMotion ? false : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} onError={() => setHasError(true)} data-id="element-41" data-mp="1wr373tdy7fcl" />}

          <button type="button" onClick={showPrevious} aria-label="Ver imagen anterior" className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-[#090B0D]/80 text-[#F8F8F6] shadow-lg backdrop-blur-sm transition-[background-color,border-color,color,transform] duration-200 hover:scale-105 hover:border-[#F2B84B]/50 hover:bg-[#1B2025] hover:text-[#F2B84B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F2B84B]/60 sm:left-4 sm:h-11 sm:w-11" data-id="element-42" data-mp="247oo3x7orc81">
            <ChevronLeft aria-hidden="true" className="h-5 w-5" data-id="element-43" data-mp="2e1p10l97llrb" />
          </button>
          <button type="button" onClick={showNext} aria-label="Ver imagen siguiente" className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-[#090B0D]/80 text-[#F8F8F6] shadow-lg backdrop-blur-sm transition-[background-color,border-color,color,transform] duration-200 hover:scale-105 hover:border-[#F2B84B]/50 hover:bg-[#1B2025] hover:text-[#F2B84B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F2B84B]/60 sm:right-4 sm:h-11 sm:w-11" data-id="element-44" data-mp="2iwuj18zhqga4">
            <ChevronRight aria-hidden="true" className="h-5 w-5" data-id="element-45" data-mp="142e26bd259p8" />
          </button>

          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-white/10 bg-[#090B0D]/80 px-3 py-1.5 font-mono text-xs tabular-nums text-[#F8F8F6] backdrop-blur-sm" data-id="element-46" data-mp="gcjro0g7nx85">
            {activeIndex + 1} / {exercisePreviews.length}
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}